import { z } from "zod";
import { CalendarDateSchema } from "../campaign-creation";
import { SmartCreationCampaignStableKeySchema } from "../smart-creation";
import {
  BudgetSchema,
  LineageKeySchema,
  SmartCreationReviewSuggestedNumericRangeSchema,
} from "./values";

export const SmartCreationReviewBudgetSuggestionSchema = z
  .object({
    algorithmVersion: z.literal(1),
    source: z.literal("same_shop_advertised_product_history"),
    status: z.enum(["suggested", "unavailable"]),
    reason: z.string().trim().min(1).nullable(),
    windowStart: CalendarDateSchema,
    windowEnd: CalendarDateSchema,
    allocations: z.array(
      z
        .object({
          campaignKey: SmartCreationCampaignStableKeySchema,
          dailyBudget: BudgetSchema,
        })
        .strict(),
    ),
  })
  .strict();
export type SmartCreationReviewBudgetSuggestion = z.infer<
  typeof SmartCreationReviewBudgetSuggestionSchema
>;

/** Read-only editor sources are separate from immutable Review snapshots. */
export const SmartCreationReviewSourcesQuerySchema = z
  .object({
    source: z.enum(["labels", "shop_keywords"]).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
    label: z.string().trim().min(1).max(100).optional(),
    search: z.string().trim().max(100).optional(),
  })
  .strict();
export type SmartCreationReviewSourcesQuery = z.infer<typeof SmartCreationReviewSourcesQuerySchema>;

export const SmartCreationReviewTargetSourceMetadataSchema = z
  .object({
    source: z.enum(["existing_keyword_tag", "existing_shop_target"]),
    suggestedBid: z.null(),
    suggestedBidRange: z.null(),
    recommendationIndex: z.null(),
    searchRank: z.null(),
    unavailableReason: z.literal("not_connected"),
  })
  .strict();

export const SmartCreationReviewSourcesSchema = z
  .object({
    capabilities: z
      .object({
        namingTemplates: z.literal("not_implemented"),
        campaignLabels: z.literal("not_implemented"),
        keywordLibrary: z.literal("not_implemented"),
        keywordTags: z.literal("available"),
        shopKeywords: z.literal("available"),
      })
      .strict(),
    portfolios: z.array(z.object({ id: z.string().uuid(), name: z.string() }).strict()),
    keywordLabels: z.array(
      z.object({ label: z.string(), count: z.number().int().positive() }).strict(),
    ),
    keywordCandidates: z.array(
      z
        .object({
          id: z.string().uuid(),
          keyword: z.string(),
          matchType: z.enum(["exact", "phrase", "broad"]),
          bid: z.number().finite().positive().nullable(),
          labels: z.array(z.string()),
          metadata: SmartCreationReviewTargetSourceMetadataSchema,
        })
        .strict(),
    ),
    pagination: z
      .object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type SmartCreationReviewSources = z.infer<typeof SmartCreationReviewSourcesSchema>;
export const SmartCreationReviewSourcesResponseSchema = z
  .object({
    success: z.literal(true),
    data: SmartCreationReviewSourcesSchema,
  })
  .strict();

/** Amazon Ads' theme-based bid preview for a not-yet-created SP Ad Group. */
export const SmartCreationReviewBidExpressionSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("keyword"),
      keyword: z.string().trim().min(1).max(80),
      matchType: z.enum(["broad", "phrase", "exact"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("automatic"),
      expressionType: z.enum(["close_match", "loose_match", "substitutes", "complements"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("product"),
      asin: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9]{10}$/),
      // PAT_ASIN has no exact/expanded discriminator in the Amazon bid contract.
      matchType: z.literal("exact").optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("category"),
      categoryId: z.string().trim().min(1).max(128),
    })
    .strict(),
]);
export const SmartCreationReviewBidPreviewInputSchema = z
  .object({
    adGroupLineageKey: LineageKeySchema,
    expressions: z.array(SmartCreationReviewBidExpressionSchema).min(1).max(100),
  })
  .strict();
export type SmartCreationReviewBidPreviewInput = z.infer<
  typeof SmartCreationReviewBidPreviewInputSchema
>;
export const SmartCreationReviewBidPreviewSchema = z
  .object({
    source: z.literal("amazon_sp_theme_based_new_ad_group"),
    items: z.array(
      z
        .object({
          expression: SmartCreationReviewBidExpressionSchema,
          suggestedBid: z.number().finite().positive(),
          range: SmartCreationReviewSuggestedNumericRangeSchema,
        })
        .strict(),
    ),
  })
  .strict();
export type SmartCreationReviewBidPreview = z.infer<typeof SmartCreationReviewBidPreviewSchema>;
export const SmartCreationReviewBidPreviewResponseSchema = z
  .object({
    success: z.literal(true),
    data: SmartCreationReviewBidPreviewSchema,
  })
  .strict();
